import React, { useState, useEffect } from 'react';
import { 
  Tag, Plus, Edit2, Trash2, Save, X, Loader2,
  CheckCircle, AlertTriangle, Palette
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Predefined color options
const COLOR_OPTIONS = [
  { name: 'Purple', value: '#9333ea' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Indigo', value: '#6366f1' },
];

/**
 * Labels Management Page
 * Full CRUD for labels with color picker
 */
const LabelsSettings = () => {
  const { token } = useAuth();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadLabels();
  }, []);

  const loadLabels = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/labels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load labels');
      const data = await response.json();
      setLabels(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load labels' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (labelData) => {
    try {
      setActionLoading('create');
      const response = await fetch(`${API_BASE}/api/labels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(labelData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create label');
      }

      setMessage({ type: 'success', text: 'Label created successfully!' });
      setShowCreateModal(false);
      loadLabels();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdate = async (labelId, labelData) => {
    try {
      setActionLoading('update');
      const response = await fetch(`${API_BASE}/api/labels/${labelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(labelData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update label');
      }

      setMessage({ type: 'success', text: 'Label updated successfully!' });
      setShowEditModal(false);
      setSelectedLabel(null);
      loadLabels();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (labelId) => {
    if (!window.confirm('Are you sure you want to delete this label? It will be removed from all leads and properties.')) {
      return;
    }

    try {
      setActionLoading(`delete_${labelId}`);
      const response = await fetch(`${API_BASE}/api/labels/${labelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete label');
      }

      setMessage({ type: 'success', text: 'Label deleted successfully!' });
      loadLabels();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="labels-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Tag size={28} className="text-teal-600" />
            Labels Management
          </h1>
          <p className="text-gray-500 mt-1">
            Create and manage labels for leads and properties
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium shadow-sm"
          data-testid="create-label-btn"
        >
          <Plus size={18} />
          Create Label
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
          message.type === 'error'
            ? 'bg-red-50 border border-red-200 text-red-600'
            : 'bg-green-50 border border-green-200 text-green-600'
        }`}>
          {message.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Labels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {labels.map((label) => (
          <div
            key={label.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
            data-testid={`label-card-${label.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: label.color + '20' }}
                >
                  <div
                    className="w-5 h-5 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{label.name}</h3>
                  <p className="text-xs text-gray-400">{label.color}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span
                className="px-3 py-1 text-sm font-medium rounded-full"
                style={{ 
                  backgroundColor: label.color + '20',
                  color: label.color
                }}
              >
                {label.name}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setSelectedLabel(label); setShowEditModal(true); }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit Label"
                  data-testid={`edit-label-${label.id}`}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(label.id)}
                  disabled={actionLoading === `delete_${label.id}`}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Label"
                  data-testid={`delete-label-${label.id}`}
                >
                  {actionLoading === `delete_${label.id}` ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {labels.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl">
            <Tag size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500">No labels created yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
            >
              Create your first label
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <LabelModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          loading={actionLoading === 'create'}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLabel && (
        <LabelModal
          label={selectedLabel}
          onClose={() => { setShowEditModal(false); setSelectedLabel(null); }}
          onSave={(data) => handleUpdate(selectedLabel.id, data)}
          loading={actionLoading === 'update'}
        />
      )}
    </div>
  );
};

/**
 * Label Create/Edit Modal
 */
const LabelModal = ({ label, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState({
    name: label?.name || '',
    color: label?.color || COLOR_OPTIONS[0].value
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Palette size={20} className="text-teal-600" />
            {label ? 'Edit Label' : 'Create Label'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Label Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              placeholder="Enter label name"
              data-testid="label-name-input"
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="grid grid-cols-5 gap-3">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  className={`w-full aspect-square rounded-xl transition-all ${
                    formData.color === color.value
                      ? 'ring-2 ring-offset-2 ring-teal-500 scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  data-testid={`color-option-${color.name}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <span
                className="px-4 py-2 text-sm font-medium rounded-full"
                style={{ 
                  backgroundColor: formData.color + '20',
                  color: formData.color
                }}
              >
                {formData.name || 'Label Name'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="save-label-btn"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              <Save size={18} />
              {label ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LabelsSettings;
